import React, { useEffect, memo } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../Layouts/Loader";
import MetaData from "../Layouts/MetaData";
import Sidebar from "./Sidebar";

// Personal Information Component
const PersonalInfo = memo(({ user, getLastName }) => (
  <div className="flex flex-col gap-5 items-start">
    <span className="font-medium text-lg">
      Personal Information{" "}
      <Link
        to="/account/update"
        className="text-sm text-primary-blue font-medium ml-8 cursor-pointer"
      >
        Edit
      </Link>
    </span>

    <div
      className="flex flex-col sm:flex-row items-center gap-3"
      id="personalInputs"
    >
      <div className="flex flex-col gap-0.5 w-64 px-3 py-1.5 rounded-sm border inputs cursor-not-allowed bg-gray-100 focus-within:border-primary-blue">
        <label className="text-xs text-gray-500">First Name</label>
        <input
          type="text"
          value={user.name.split(" ", 1)}
          className="text-sm outline-none border-none cursor-not-allowed text-gray-500"
          disabled
        />
      </div>
      <div className="flex flex-col gap-0.5 w-64 px-3 py-1.5 rounded-sm border inputs cursor-not-allowed bg-gray-100 focus-within:border-primary-blue">
        <label className="text-xs text-gray-500">Last Name</label>
        <input
          type="text"
          value={getLastName()}
          className="text-sm outline-none border-none cursor-not-allowed text-gray-500"
          disabled
        />
      </div>
    </div>

    <div className="flex flex-col gap-2">
      <h2 className="text-sm">Your Gender</h2>
      <div className="flex items-center gap-8" id="radioInput">
        <div className="flex items-center gap-4 inputs text-gray-500 cursor-not-allowed">
          <input
            type="radio"
            name="gender"
            checked={user.gender === "male"}
            id="male"
            className="h-4 w-4 cursor-not-allowed"
            disabled
          />
          <label htmlFor="male" className="cursor-not-allowed">
            Male
          </label>
        </div>
        <div className="flex items-center gap-4 inputs text-gray-500 cursor-not-allowed">
          <input
            type="radio"
            name="gender"
            checked={user.gender === "female"}
            id="female"
            className="h-4 w-4 cursor-not-allowed"
            disabled
          />
          <label htmlFor="female" className="cursor-not-allowed">
            Female
          </label>
        </div>
      </div>
    </div>
  </div>
));

const EmailInfo = memo(({ user }) => (
  <div className="flex flex-col gap-5 items-start">
    <span className="font-medium text-lg">
      Email Address
      <Link
        to="/account/update"
        className="text-sm text-primary-blue font-medium ml-3 sm:ml-8 cursor-pointer"
      >
        Edit
      </Link>
      <Link
        to="/password/update"
        className="text-sm text-primary-blue font-medium ml-3 sm:ml-8"
      >
        Change Password
      </Link>
    </span>

    <div className="flex items-center gap-3">
      <div className="flex flex-col gap-0.5 sm:w-64 px-3 py-1.5 rounded-sm border bg-gray-100 cursor-not-allowed focus-within:border-primary-blue">
        <label className="text-xs text-gray-500">Email Address</label>
        <input
          type="email"
          value={user.email}
          className="text-sm outline-none border-none cursor-not-allowed text-gray-500"
          disabled
        />
      </div>
    </div>
  </div>
));

const Account = () => {
  const navigate = useNavigate();
  const { user, loading, isAuthenticated } = useSelector((state) => state.user);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const getLastName = () => {
    const nameArray = user.name.split(" ");
    return nameArray[nameArray.length - 1];
  };

  return (
    <>
      <MetaData
        title="My Profile | Siddhi Creatives"
        description="View and edit your personal information, settings, and preferences on Siddhi Creatives. Manage your account to stay updated with our latest creative services."
        keywords="my profile, account settings, user profile, manage account, edit profile, Siddhi Creatives, personal settings"
        ogTitle="My Profile | Siddhi Creatives"
        ogDescription="Access and update your personal information, settings, and preferences on Siddhi Creatives. Manage your profile for a personalized experience."
        ogUrl="https://www.siddhicreatives.com/account"
      />

      {loading ? (
        <Loader />
      ) : (
        <main className="w-full mt-1 sm:mt-24">
          <div className="flex gap-3.5 mt-20 sm:mt-6 sm:mx-3 m-auto mb-7">
            <Sidebar activeTab="profile" />

            <div className="flex-1 overflow-hidden shadow bg-white">
              <div className="flex flex-col gap-12 m-4 sm:mx-8 sm:my-6">
                {/* Personal Info Section */}
                <PersonalInfo user={user} getLastName={getLastName} />

                {/* Email Info Section */}
                <EmailInfo user={user} />
              </div>
            </div>
          </div>
        </main>
      )}
    </>
  );
};

export default Account;
